/**
 * fetchWithAuth.ts
 * ────────────────
 * Attaches the server access token (from session store) to every request.
 * On 401, attempts to silently refresh (device re-auth or cookie rotation)
 * and retries once.
 *
 * Token resolution order (matches backend priority):
 *   1. device mode  → server access token (stored after POST /api/devices/auth)
 *   2. server mode  → server access token (stored after GET /api/auth/session)
 *   3. none         → no Authorization header (public endpoints only)
 *
 * Auth0 is NOT consulted here — it was only used during bootstrap.
 */

import { getSession, setSession } from "@/lib/sessionStore";

// Dedup concurrent refresh calls
let _refreshInFlight: Promise<string | null> | null = null;

/** Rotate the refresh cookie and get a new access token. */
async function refreshFromCookie(): Promise<string | null> {
  if (_refreshInFlight) return _refreshInFlight;

  _refreshInFlight = (async () => {
    try {
      const res = await fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data.accessToken) {
        setSession({
          accessToken: data.accessToken,
          ...(data.user ? { user: { ...getSession().user, ...data.user } } : {}),
        });
        return data.accessToken;
      }
      return null;
    } catch {
      return null;
    } finally {
      _refreshInFlight = null;
    }
  })();

  return _refreshInFlight;
}

/** Re-authenticate using the stored device token. */
async function refreshDeviceSession(): Promise<string | null> {
  const raw = localStorage.getItem("showme_device_token");
  if (!raw) return null;

  try {
    const res = await fetch("/api/devices/auth", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceToken: raw }),
    });
    if (!res.ok) {
      if (res.status === 401) localStorage.removeItem("showme_device_token");
      return null;
    }
    const data = await res.json();
    if (data.accessToken) {
      setSession({ accessToken: data.accessToken });
      return data.accessToken;
    }
    return null;
  } catch {
    return null;
  }
}

/** Build request headers with Authorization + Content-Type. */
function buildHeaders(token: string | null, base: HeadersInit | undefined): Headers {
  const headers = new Headers(base || {});
  if (token?.trim()) headers.set("Authorization", `Bearer ${token}`);
  return headers;
}

/**
 * fetchWithAuth — the single authenticated fetch function used everywhere.
 * Sets Content-Type: application/json automatically for string bodies.
 */
export async function fetchWithAuth(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const session = getSession();
  const token = session.accessToken;

  if (!token) {
    console.log(`[fetchWithAuth] No token — ${url}`);
  }

  // Determine once whether Content-Type needs to be injected
  const needsContentType =
    !!options.body &&
    typeof options.body === "string" &&
    !buildHeaders(null, options.headers).has("Content-Type");

  const doFetch = (tok: string | null) => {
    const headers = buildHeaders(tok, options.headers);
    if (needsContentType) headers.set("Content-Type", "application/json");
    return fetch(url, {
      ...options,
      headers,
      credentials: "include",
    });
  };

  let response = await doFetch(token);

  // 401 → refresh and retry once
  if (response.status === 401 && !url.includes("/api/auth/refresh") && !url.includes("/api/devices/auth")) {
    const newToken =
      session.mode === "device"
        ? await refreshDeviceSession()
        : await refreshFromCookie();

    if (newToken) {
      response = await doFetch(newToken);
    }
  }

  if (!response.ok) {
    console.warn(`[fetchWithAuth] ${response.status} ${response.statusText} — ${url}`);
  }

  return response;
}

/** Hook wrapper (unchanged signature for compatibility) */
export function useFetchWithAuth() {
  return fetchWithAuth;
}
